export const PDF_ROTATION_STEP_DEGREES = 90;

export const normalizePdfRotation = (rotationDegrees) => (
  ((Number(rotationDegrees) || 0) % 360 + 360) % 360
);

const getOptionalPdfRotation = (rotationDegrees) => {
  if (rotationDegrees === null || rotationDegrees === undefined || rotationDegrees === '') {
    return null;
  }
  const numericRotation = Number(rotationDegrees);
  if (!Number.isFinite(numericRotation)) return null;
  const normalizedRotation = normalizePdfRotation(numericRotation);
  return normalizedRotation % PDF_ROTATION_STEP_DEGREES === 0 ? normalizedRotation : null;
};

export const getPdfPageRotationState = ({
  pdfPageRotation = 0,
  visionRotation = 0,
  correctionRotation = null,
  metadataPdfRotation = null,
  finalRotation = null,
  rotationBakedIntoPdf = false,
  viewerRotation = 0,
  pageOnlyRotation = 0,
} = {}) => {
  const normalizedPdfPageRotation = normalizePdfRotation(pdfPageRotation);
  const requestedCorrection = correctionRotation === null || correctionRotation === undefined
    ? visionRotation
    : correctionRotation;
  const normalizedCorrectionRotation = rotationBakedIntoPdf
    ? 0
    : normalizePdfRotation(requestedCorrection);
  const normalizedMetadataPdfRotation = getOptionalPdfRotation(metadataPdfRotation);
  const normalizedFinalRotation = getOptionalPdfRotation(finalRotation);
  const metadataMatchesPdf = normalizedMetadataPdfRotation === null
    || normalizedMetadataPdfRotation === normalizedPdfPageRotation;
  const calculatedFinalRotation = normalizePdfRotation(
    normalizedPdfPageRotation + normalizedCorrectionRotation,
  );
  const resolvedFinalRotation = normalizedFinalRotation !== null && metadataMatchesPdf
    ? normalizedFinalRotation
    : calculatedFinalRotation;
  const interactiveRotation = normalizePdfRotation(
    normalizePdfRotation(viewerRotation) + normalizePdfRotation(pageOnlyRotation),
  );

  return {
    pdfRotation: normalizedPdfPageRotation,
    correctionRotation: normalizedCorrectionRotation,
    finalRotation: resolvedFinalRotation,
    interactiveRotation,
    renderRotation: normalizePdfRotation(resolvedFinalRotation + interactiveRotation),
    // Coordinates are stored in PyMuPDF's rendered page space, which already
    // includes native PDF /Rotate. Only later transforms apply to tag boxes.
    bboxRotation: normalizePdfRotation(normalizedCorrectionRotation + interactiveRotation),
  };
};

export const getEffectivePdfPageRotation = (rotationOptions = {}) => {
  return getPdfPageRotationState(rotationOptions).renderRotation;
};

export const getNextPdfRotation = (rotationDegrees) => (
  normalizePdfRotation(Number(rotationDegrees) + PDF_ROTATION_STEP_DEGREES)
);

export const rotatePdfPageInMap = (rotations, pageNumber) => {
  const next = { ...rotations };
  const rotation = getNextPdfRotation(normalizePdfRotation(next[pageNumber]));
  if (rotation) next[pageNumber] = rotation;
  else delete next[pageNumber];
  return next;
};

export const getRotatedPdfThumbnailScale = (width, height, rotationDegrees) => {
  const radians = normalizePdfRotation(rotationDegrees) * Math.PI / 180;
  const cosine = Math.abs(Math.cos(radians));
  const sine = Math.abs(Math.sin(radians));
  const rotatedWidth = width * cosine + height * sine;
  const rotatedHeight = width * sine + height * cosine;
  return Math.min(1, width / rotatedWidth, height / rotatedHeight);
};
